import React from "react";

import * as echarts from "echarts";
import ReactECharts from "echarts-for-react";

import { animated, useSpring } from "react-spring"; // For animated number

import { useTheme } from "@mui/material/styles";
import { Box, Chip, CircularProgress, Divider, FormControl, Grid, MenuItem, Select, Typography } from "@mui/material";

import PaperFragment from "../../../../helper-components/misc/PaperFragment";

import { standardGetQuery } from "../../../../helper-functions/api/apiUtils";
import { queryAuthorStatistics, queryHighlightStatistics, queryResourceStatistics, queryTranscriptStatistics } from "../../../../helper-functions/api/backendQueries";
import { getLanguage } from "../../../../config/language/language";
import * as Constants from "../../../../config/constants/Constants";

/**
 * Statistics component, displays various statistics about the resources, transcripts, and authors in the database.
 */
export default function StatisticsForm(props) {
    const isMobile = props.browser.isMobile;
    const [libraries, setLibraries] = React.useState([]);
    const propsRef = React.useRef(props);
    propsRef.current = props;

    React.useEffect(() => {
        standardGetQuery(propsRef.current, `${Constants.API_LIBRARIES}?page=0&page_size=500&order_by=name&order=asc`, (response) => {
            const rows = response.body?.elements || [];
            setLibraries([...rows].sort((a, b) => (a.name || "").localeCompare(b.name || "")));
        });
    }, []);

    const onLibraryChange = (event) => {
        const value = event.target.value;
        props.backend.setStatisticsLibraryId(value === "" ? null : Number(value));
        queryResourceStatistics(props, { once: true });
        queryTranscriptStatistics(props, { once: true });
        queryAuthorStatistics(props, { once: true });
        queryHighlightStatistics(props, { once: true });
    };

    const resourceStatistics = props.backend.resourceStatistics;
    const transcriptStatistics = props.backend.transcriptStatistics;
    const authorStatistics = props.backend.authorStatistics;
    const highlightStatistics = props.backend.highlightStatistics;

    /**
     * E.g. 3600 -> 1, 3700 -> 1.02
     */
    const convertSecondsToHours = (seconds = 0) => {
        if (seconds === 0) return 0;
        const hours = seconds / 3600;
        return parseFloat(hours.toFixed(1)); // Round to 1 decimal place
    };

    const resourceTotal = resourceStatistics?.total || 0;
    const transcriptTotal = transcriptStatistics?.total || 0;
    const transcriptDurationTotal = transcriptStatistics?.duration || 0;
    const authorTotal = authorStatistics?.total || 0;
    const highlightTotal = highlightStatistics?.total || 0;
    const highlightReviewTotal = highlightStatistics?.total_reviews || 0;
    const highlightRequestTotal = highlightStatistics?.total_requests || 0;

    // Animation for total number
    const { number: resourceTotalEl } = useSpring({ from: { number: 0 }, number: resourceTotal, config: { duration: 1000 } });
    const { number: transcriptTotalEl } = useSpring({ from: { number: 0 }, number: transcriptTotal, config: { duration: 1400 } });
    const { number: transcriptDurationTotalEl } = useSpring({ from: { number: 0 }, number: transcriptDurationTotal, config: { duration: 600 } });
    const { number: authorTotalEl } = useSpring({ from: { number: 0 }, number: authorTotal, config: { duration: 600 } });
    const { number: highlightTotalEl } = useSpring({ from: { number: 0 }, number: highlightTotal, config: { duration: 500 } });
    const { number: highlightReviewTotalEl } = useSpring({ from: { number: 0 }, number: highlightReviewTotal, config: { duration: 400 } });
    const { number: highlightRequestTotalEl } = useSpring({ from: { number: 0 }, number: highlightRequestTotal, config: { duration: 500 } });

    const isLoading = !resourceStatistics || !transcriptStatistics || !authorStatistics || !highlightStatistics;

    const theme = useTheme();
    const chartTheme = theme.palette.mode === "dark" ? "dark" : undefined;

    // Set the theme for ECharts based on the current theme mode
    echarts.registerTheme("dark", {
        backgroundColor: "#1e1e1e",
        textStyle: {
            color: "#ffffff"
        },
        legend: {
            textStyle: {
                color: "#ffffff"
            }
        }
    });

    const getColorByStatus = (status) => {
        switch (status) {
            case "AWAITING_APPROVAL":
                return "rgb(250,200,88)";
            case "IN_QUEUE":
                return "rgb(173, 223, 252)";
            case "PROCESSED":
                return "rgb(127, 187, 99)";
            case "ERROR":
                return "rgb(219,44,44)";
            case "IN_PROGRESS":
                return "rgb(84,112,198)";

            case "APPROVED":
                return "rgb(84,112,198)";
            case "REJECTED":
                return "rgb(219,44,44)";
            case "COMPLETED":
                return "rgb(127, 187, 99)";

            default:
                return undefined;
        }
    };

    const getColorByType = (status) => {
        switch (status) {
            case "UNCATEGORIZED":
                return "rgb(153, 152, 150)";
            case "LECTURE":
                return "rgb(119, 184, 221)";
            case "PODCAST":
                return "rgb(230, 133, 7)";
            case "SHORT":
                return "rgb(206, 133, 221)";
            case "INTERVIEW":
                return "rgb(76, 175, 80)";
            case "DEBATE":
                return "rgb(233, 30, 99)";
            default:
                return undefined;
        }
    };

    const formatName = (name) => {
        // Split by underscore and capitalize first letter of each word
        const words = name.split("_").map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase());
        return words.join(" ");
    };

    const uploadedOnChartOption = (statistics) => {
        // Extract the dates from the data
        const dates = statistics.by_uploaded_on.map((item) => item[0]);

        // Create a continuous date range
        let allDates = [];
        if (dates.length > 0) {
            const startDate = new Date(dates[0]);
            const endDate = new Date(dates[dates.length - 1]);

            // Generate all dates between start and end
            for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
                allDates.push(d.toISOString().split("T")[0]); // Format as YYYY-MM-DD
            }
        }

        // Create a map of existing data points
        const dataMap = {};
        statistics.by_uploaded_on.forEach((item) => {
            dataMap[item[0]] = item[1];
        });

        // Create the complete dataset with zeros for missing dates
        const completeData = allDates.map((date) => [date, dataMap[date] || 0]);

        return {
            tooltip: {},
            grid: {
                top: "5%",
                bottom: "5%",
                left: "5%",
                right: "5%",
                height: "80%"
            },
            xAxis: {
                type: "category",
                data: completeData.map((item) => item[0]),
                boundaryGap: false
            },
            yAxis: {
                type: "value",
                min: 0
            },
            series: [
                {
                    name: "Uploaded",
                    type: "bar",
                    data: completeData.map((item) => item[1])
                }
            ]
        };
    };

    const statusChartOption = (statistics) => {
        return {
            tooltip: {
                trigger: "item",
                formatter: "{a} <br/>{b}: {c} ({d}%)"
            },
            legend: {
                orient: "vertical",
                left: "left",
                data: statistics.by_status.map((item) => formatName(item[0]))
            },
            series: [
                {
                    name: "Status",
                    type: "pie",
                    radius: ["0%", "50%"],
                    avoidLabelOverlap: false,
                    label: {
                        show: true,
                        position: "outside",
                        formatter: "{b}: {c} ({d}%)"
                    },
                    emphasis: {
                        label: {
                            show: true,
                            fontSize: "12",
                            fontWeight: "bold"
                        }
                    },
                    labelLine: {
                        show: true
                    },
                    data: statistics.by_status.map((item) => ({
                        value: item[1],
                        name: formatName(item[0]),
                        itemStyle: {
                            color: getColorByStatus(item[0])
                        }
                    }))
                }
            ]
        };
    };

    const createdOnChartOption = (statistics) => {
        return {
            tooltip: {},
            grid: {
                top: "5%",
                bottom: "10%"
            },
            xAxis: {
                type: "category",
                data: statistics.by_created_on.map((item) => item[0]),
                boundaryGap: false
            },
            yAxis: {
                type: "value"
            },
            series: [
                {
                    name: "Created",
                    type: "line",
                    data: statistics.by_created_on.map((item) => item[1])
                }
            ]
        };
    };

    const typeChartOption = (statistics) => {
        return {
            tooltip: {
                trigger: "item",
                formatter: "{a} <br/>{b}: {c} ({d}%)"
            },
            legend: {
                orient: "vertical",
                left: "left",
                data: statistics.by_type.map((item) => formatName(item[0]))
            },
            series: [
                {
                    name: "Type",
                    type: "pie",
                    radius: ["0%", "50%"],
                    avoidLabelOverlap: false,
                    label: {
                        show: true,
                        position: "outside",
                        formatter: "{b}: {c} ({d}%)"
                    },
                    emphasis: {
                        label: {
                            show: true,
                            fontSize: "12",
                            fontWeight: "bold"
                        }
                    },
                    labelLine: {
                        show: true
                    },
                    data: statistics.by_type.map((item) => ({
                        value: item[1],
                        name: formatName(item[0]),
                        itemStyle: {
                            color: getColorByType(item[0])
                        }
                    }))
                }
            ]
        };
    };
    const resourcesChartOption = (statistics) => {
        return {
            tooltip: {},
            grid: {
                top: "5%",
                bottom: "15%",
                left: "5%",
                right: "5%",
                height: "70%"
            },
            xAxis: {
                type: "category",
                data: statistics.by_resources.map((item) => `${item?.first_name?.[0]}. ${item?.last_name}`),
                axisLabel: {
                    rotate: 65, // Rotate labels
                    textStyle: {
                        fontSize: 11 // Smaller font size
                    }
                }
            },
            yAxis: {
                type: "value"
            },
            series: [
                {
                    name: "Resources",
                    type: "bar",
                    data: statistics.by_resources.map((item) => item.count)
                }
            ]
        };
    };

    return (
        <Grid container pl={1}>
            <Grid item xs={12}>
                <PaperFragment
                    title={getLanguage().STATISTICS_LIBRARY_FILTER}
                    value={
                        <Box sx={{ width: "100%" }}>
                            <Typography variant="caption" sx={{ display: "block", mb: 1 }}>
                                {getLanguage().STATISTICS_LIBRARY_FILTER_HELP}
                            </Typography>
                            <FormControl fullWidth size="small">
                                <Select
                                    value={props.backend.statisticsLibraryId == null ? "" : String(props.backend.statisticsLibraryId)}
                                    onChange={onLibraryChange}
                                    displayEmpty
                                    inputProps={{ "aria-label": getLanguage().STATISTICS_LIBRARY_FILTER }}
                                >
                                    <MenuItem value="">{getLanguage().STATISTICS_LIBRARY_ALL}</MenuItem>
                                    {libraries.map((library) => (
                                        <MenuItem key={library.id} value={String(library.id)}>
                                            {library.name || library.url}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                    }
                />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
                <PaperFragment
                    center
                    height={isMobile ? "75px" : "125px"}
                    title="Total authors"
                    value={<animated.div style={{ fontSize: "1.5em", fontWeight: "bold" }}>{authorTotalEl.to((n) => Math.floor(n))}</animated.div>}
                />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
                <PaperFragment
                    center
                    height={isMobile ? "75px" : "125px"}
                    title="Total resources"
                    value={<animated.div style={{ fontSize: "1.5em", fontWeight: "bold" }}>{resourceTotalEl.to((n) => Math.floor(n))}</animated.div>}
                />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
                <PaperFragment
                    center
                    height={isMobile ? "75px" : "125px"}
                    title="Total transcripts"
                    value={<animated.div style={{ fontSize: "1.5em", fontWeight: "bold" }}>{transcriptTotalEl.to((n) => Math.floor(n))}</animated.div>}
                />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
                <PaperFragment
                    center
                    height={isMobile ? "75px" : "125px"}
                    title="Transcribed hours"
                    value={<animated.div style={{ fontSize: "1.5em", fontWeight: "bold" }}>{transcriptDurationTotalEl.to((n) => convertSecondsToHours(n))}</animated.div>}
                />
            </Grid>

            {isLoading && (
                <Grid item xs={12}>
                    <Box display="flex" justifyContent="center" alignItems="center" sx={{ mt: 2, height: "200px" }}>
                        <CircularProgress />
                    </Box>
                </Grid>
            )}

            {!isLoading && (
                <>
                    <Grid item xs={12}>
                        <Divider sx={{ m: 1 }}>
                            <Chip label="Resource Statistics" variant="outlined" size="small" />
                        </Divider>
                    </Grid>

                    <Grid item xs={12} sm={12}>
                        <PaperFragment title="Resources Uploaded Over Time" value={<ReactECharts theme={chartTheme} option={uploadedOnChartOption(resourceStatistics)} />} />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <PaperFragment title="Resources By Status" value={<ReactECharts theme={chartTheme} option={statusChartOption(resourceStatistics)} />} />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <PaperFragment title="Resources By Type" value={<ReactECharts theme={chartTheme} option={typeChartOption(resourceStatistics)} />} />
                    </Grid>

                    <Grid item xs={12}>
                        <Divider sx={{ m: 1 }}>
                            <Chip label="Transcript Statistics" variant="outlined" size="small" />
                        </Divider>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <PaperFragment title="Transcripts Created Over Time" value={<ReactECharts theme={chartTheme} option={createdOnChartOption(transcriptStatistics)} />} />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <PaperFragment title="Transcripts By Status" value={<ReactECharts theme={chartTheme} option={statusChartOption(transcriptStatistics)} />} />
                    </Grid>

                    <Grid item xs={12}>
                        <Divider sx={{ m: 1 }}>
                            <Chip label="Author Statistics" variant="outlined" size="small" />
                        </Divider>
                    </Grid>

                    <Grid item xs={12} sm={12}>
                        <PaperFragment
                            title="Authors By Resources"
                            value={
                                <ReactECharts
                                    theme={chartTheme}
                                    option={resourcesChartOption(authorStatistics)}
                                    style={{
                                        height: "500px"
                                    }}
                                />
                            }
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Divider sx={{ m: 1 }}>
                            <Chip label="Highlight Statistics" variant="outlined" size="small" />
                        </Divider>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <PaperFragment title="Highlights By Status" value={<ReactECharts theme={chartTheme} option={statusChartOption(highlightStatistics)} />} />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <>
                            <PaperFragment
                                center
                                height={isMobile ? "75px" : "112px"}
                                title="Total requests"
                                value={<animated.div style={{ fontSize: "1.5em", fontWeight: "bold" }}>{highlightRequestTotalEl.to((n) => Math.floor(n))}</animated.div>}
                            />

                            <PaperFragment
                                center
                                height={isMobile ? "75px" : "112px"}
                                title="Total highlights"
                                value={<animated.div style={{ fontSize: "1.5em", fontWeight: "bold" }}>{highlightTotalEl.to((n) => Math.floor(n))}</animated.div>}
                            />

                            <PaperFragment
                                center
                                height={isMobile ? "75px" : "112px"}
                                title="Total reviews"
                                value={<animated.div style={{ fontSize: "1.5em", fontWeight: "bold" }}>{highlightReviewTotalEl.to((n) => Math.floor(n))}</animated.div>}
                            />
                        </>
                    </Grid>
                </>
            )}
        </Grid>
    );
}
